import React, { useState, useEffect, useMemo } from 'react';
import { getPokemonPool } from '../../data/regulations';
import { MOVES } from '../../data/moves';
import { calculateDamageRange, calculateStat } from '../../utils/damageCalculator';
import { WEATHER, TERRAIN } from '../../data/battleConditions';
import { WEATHER_SETTERS, TERRAIN_SETTERS, MOVETYPE_WEATHER } from '../../data/commonSets';
import QuizModal from './QuizModal';
import './DamageCalcQuiz.css';

function DamageCalcQuiz({ isOpen, onClose, regulation = 'all' }) {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [userPrediction, setUserPrediction] = useState(0);
  const [isCorrect, setIsCorrect] = useState(false);

  const attackerPool = useMemo(
    () => getPokemonPool(regulation).filter(p => p.commonMoves && p.commonMoves.length > 0),
    [regulation]
  );

  useEffect(() => {
    if (isOpen && !currentQuestion) {
      generateQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);


  const generateQuestion = () => {
    setShowFeedback(false);
    setUserPrediction(0);

    // Pick random attacker; keep re-picking defender until it differs
    const attacker = attackerPool[Math.floor(Math.random() * attackerPool.length)];
    let defender;
    do {
      defender = attackerPool[Math.floor(Math.random() * attackerPool.length)];
    } while (defender.name === attacker.name);

    const atkBS = attacker.baseStats;
    const defBS = defender.baseStats;

    // Generate random EVs
    const attackerEVs = {
      hp: Math.floor(Math.random() * 252),
      attack: Math.floor(Math.random() * 252),
      defense: Math.floor(Math.random() * 252),
      specialAttack: Math.floor(Math.random() * 252),
      specialDefense: Math.floor(Math.random() * 252),
      speed: Math.floor(Math.random() * 252)
    };
    const defenderEVs = {
      hp: Math.floor(Math.random() * 252),
      attack: Math.floor(Math.random() * 252),
      defense: Math.floor(Math.random() * 252),
      specialAttack: Math.floor(Math.random() * 252),
      specialDefense: Math.floor(Math.random() * 252),
      speed: Math.floor(Math.random() * 252)
    };

    // Calculate final stats
    const attackerStats = {
      hp:             calculateStat(atkBS.hp,  attackerEVs.hp,            31, 50, 'Serious', 'hp'),
      attack:         calculateStat(atkBS.atk, attackerEVs.attack,         31, 50, 'Serious', 'attack'),
      defense:        calculateStat(atkBS.def, attackerEVs.defense,        31, 50, 'Serious', 'defense'),
      specialAttack:  calculateStat(atkBS.spa, attackerEVs.specialAttack,  31, 50, 'Serious', 'specialAttack'),
      specialDefense: calculateStat(atkBS.spd, attackerEVs.specialDefense, 31, 50, 'Serious', 'specialDefense'),
      speed:          calculateStat(atkBS.spe, attackerEVs.speed,          31, 50, 'Serious', 'speed'),
    };
    const defenderStats = {
      hp:             calculateStat(defBS.hp,  defenderEVs.hp,            31, 50, 'Serious', 'hp'),
      attack:         calculateStat(defBS.atk, defenderEVs.attack,         31, 50, 'Serious', 'attack'),
      defense:        calculateStat(defBS.def, defenderEVs.defense,        31, 50, 'Serious', 'defense'),
      specialAttack:  calculateStat(defBS.spa, defenderEVs.specialAttack,  31, 50, 'Serious', 'specialAttack'),
      specialDefense: calculateStat(defBS.spd, defenderEVs.specialDefense, 31, 50, 'Serious', 'specialDefense'),
      speed:          calculateStat(defBS.spe, defenderEVs.speed,          31, 50, 'Serious', 'speed'),
    };

    // Select a valid move
    const validMoves = (attacker.commonMoves || []).filter(moveName => {
      const move = MOVES[moveName];
      return move && move.name && move.type && move.category && move.basePower;
    });
    if (validMoves.length === 0) { generateQuestion(); return; }

    const moveName = validMoves[Math.floor(Math.random() * validMoves.length)];
    const move = MOVES[moveName];

    const hasTeraAttacker = Math.random() < 0.3;

    // Weather: weather setters bring their own; otherwise use move type as a hint
    let weatherType = null;
    const preferredWeatherCalc = WEATHER_SETTERS[attacker.name];
    const moveWeatherHint = MOVETYPE_WEATHER[move.type];
    if (preferredWeatherCalc && Math.random() < 0.80) {
      weatherType = preferredWeatherCalc;
    } else if (moveWeatherHint && Math.random() < 0.40) {
      weatherType = moveWeatherHint;
    } else if (Math.random() < 0.20) {
      const weatherKeys = Object.keys(WEATHER);
      weatherType = weatherKeys[Math.floor(Math.random() * weatherKeys.length)];
    }

    // Terrain: terrain setters almost always have their terrain
    let terrainType = null;
    const preferredTerrainCalc = TERRAIN_SETTERS[attacker.name] || TERRAIN_SETTERS[defender.name];
    if (preferredTerrainCalc && Math.random() < 0.75) {
      terrainType = preferredTerrainCalc;
    } else if (Math.random() < 0.15) {
      const terrainKeys = Object.keys(TERRAIN);
      terrainType = terrainKeys[Math.floor(Math.random() * terrainKeys.length)];
    }

    const conditions = {
      weather: weatherType,
      terrain: terrainType
    };

    const damageRange = calculateDamageRange(
      { 
        ...attacker, 
        stats: attackerStats,
        evs: attackerEVs,
        nature: 'Serious'
      },
      { 
        ...defender, 
        stats: defenderStats,
        evs: defenderEVs,
        nature: 'Serious'
      },
      move,
      conditions
    );

    setCurrentQuestion({
      attacker: {
        ...attacker,
        stats: attackerStats,
        evs: attackerEVs,
        nature: 'Serious',
        sprites: {
          frontDefault: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${attacker.spriteId ?? attacker.id}.png`,
          fallback: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${attacker.spriteId ?? attacker.id}.png`,
        },
      },
      defender: {
        ...defender,
        stats: defenderStats,
        evs: defenderEVs,
        nature: 'Serious',
        sprites: {
          frontDefault: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${defender.spriteId ?? defender.id}.png`,
          fallback: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${defender.spriteId ?? defender.id}.png`,
        },
      },
      move,
      conditions,
      damageRange,
      teraType: hasTeraAttacker ? move.type : null,
    });

    setTotalQuestions(prev => prev + 1);
  };

  const handleSliderChange = (event) => {
    const value = parseInt(event.target.value);
    setUserPrediction(value);
  };

  const handleSubmit = () => {
    if (userPrediction === null || showFeedback) return;

    const maxHP = currentQuestion.defender.stats.hp;
    const actualMin = currentQuestion.damageRange.min;
    const actualMax = currentQuestion.damageRange.max;

    // Cap actual range at 100% of HP so over-KO damage is always answerable with 100
    const cappedMin = Math.min(actualMin, maxHP);
    const cappedMax = Math.min(actualMax, maxHP);
    const actualAverage = (cappedMin + cappedMax) / 2;
    const predictedDamage = (userPrediction / 100) * maxHP;

    // Allow for a 15% margin of error
    const isWithinRange = Math.abs(predictedDamage - actualAverage) <= (actualAverage * 0.15);

    setIsCorrect(isWithinRange);
    if (isWithinRange) {
      setScore(prev => prev + 1);
    }
    setShowFeedback(true);
  };

  if (!isOpen) {
    return null;
  }

  if (!currentQuestion) {
    return (
      <QuizModal isOpen={isOpen} onClose={onClose}>
        <div className="damage-calc-quiz-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading question...</p>
          </div>
        </div>
      </QuizModal>
    );
  }

  const maxHP = currentQuestion.defender.stats.hp;
  const minDamagePercent = Math.min(100, Math.round((currentQuestion.damageRange.min / maxHP) * 100));
  const maxDamagePercent = Math.min(100, Math.round((currentQuestion.damageRange.max / maxHP) * 100));

  return (
    <QuizModal isOpen={isOpen} onClose={onClose}>
      <div className="damage-calc-quiz-container">
        <div className="quiz-header">
          <h3>Damage Calculator Quiz</h3>
          <div className="quiz-controls">
            <div className="score">Score: {score}/{totalQuestions}</div>
            {showFeedback && (
              <button className="next-question-arrow" onClick={generateQuestion}>
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                </svg>
              </button>
            )}
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="battle-scene">
          <div className="pokemon attacker">
            <img
              src={currentQuestion.attacker.sprites.frontDefault}
              alt={currentQuestion.attacker.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = currentQuestion.attacker.sprites.fallback;
              }}
              className="fade-in"
            />
            <h4>{currentQuestion.attacker.name}</h4>
            <div className="pokemon-details">
              <div className="stat-row">
                <span className="stat-label">Nature</span>
                <span className="stat-value">{currentQuestion.attacker.nature}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">{currentQuestion.move.category === 'Physical' ? 'Atk' : 'Sp.Atk'} EVs</span>
                <span className={`ev-chip ${(currentQuestion.attacker.evs[currentQuestion.move.category.toLowerCase() === 'physical' ? 'attack' : 'specialAttack'] || 0) >= 128 ? 'ev-high' : 'ev-low'}`}>
                  {currentQuestion.attacker.evs[currentQuestion.move.category.toLowerCase() === 'physical' ? 'attack' : 'specialAttack']}
                </span>
              </div>
              {currentQuestion.teraType && (
                <div className="stat-row">
                  <span className="stat-label">Tera</span>
                  <span className="stat-value">{currentQuestion.teraType}</span>
                </div>
              )}
            </div>
          </div>

          <div className="move-info">
            <h4>{currentQuestion.move.name}</h4>
            <p>Base Power: {currentQuestion.move.basePower}</p>
            <p>Type: {currentQuestion.move.type}</p>
            <p>Category: {currentQuestion.move.category}</p>
            {currentQuestion.conditions.weather && (
              <p>Weather: {currentQuestion.conditions.weather}</p>
            )}
            {currentQuestion.conditions.terrain && (
              <p>Terrain: {currentQuestion.conditions.terrain}</p>
            )}
          </div>

          <div className="pokemon defender">
            <img
              src={currentQuestion.defender.sprites.frontDefault}
              alt={currentQuestion.defender.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = currentQuestion.defender.sprites.fallback;
              }}
              className="fade-in"
            />
            <h4>{currentQuestion.defender.name}</h4>
            <div className="pokemon-details">
              <div className="stat-row">
                <span className="stat-label">Nature</span>
                <span className="stat-value">{currentQuestion.defender.nature}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">{currentQuestion.move.category === 'Physical' ? 'Def' : 'Sp.Def'} EVs</span>
                <span className={`ev-chip ${(currentQuestion.defender.evs[currentQuestion.move.category.toLowerCase() === 'physical' ? 'defense' : 'specialDefense'] || 0) >= 128 ? 'ev-high' : 'ev-low'}`}>
                  {currentQuestion.defender.evs[currentQuestion.move.category.toLowerCase() === 'physical' ? 'defense' : 'specialDefense']}
                </span>
              </div>
              <div className="stat-row">
                <span className="stat-label">HP</span>
                <span className="stat-value">{currentQuestion.defender.stats.hp}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="damage-prediction">
          <div className="slider-container">
            <div className="slider-track"></div>

            {!showFeedback ? (
              <>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={userPrediction}
                  onChange={handleSliderChange}
                  className="damage-slider"
                />
              </>
            ) : (
              <>
                <div 
                  className="prediction-marker user-prediction"
                  style={{ left: `${userPrediction}%` }}
                />
                <div 
                  className="range-marker min-marker"
                  style={{ left: `${minDamagePercent}%` }}
                />
                <div 
                  className="range-marker max-marker"
                  style={{ left: `${maxDamagePercent}%` }}
                />
                <div 
                  className="actual-damage-range"
                  style={{ 
                    left: `${minDamagePercent}%`,
                    width: `${maxDamagePercent - minDamagePercent}%`
                  }}
                >
                  <span className="range-label">{minDamagePercent}%-{maxDamagePercent}%</span>
                </div>
              </>
            )}
          </div>
          <div className="slider-ticks">
            <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
          </div>
          <button
            className={`submit-button ${showFeedback ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={showFeedback}
          >
            Submit Prediction
          </button>
        </div>

        {showFeedback && (
          <div className="feedback-container">
            <div className={`feedback ${isCorrect ? 'correct' : 'incorrect'}`}>
              <h4>{isCorrect ? 'Correct!' : 'Incorrect!'}</h4>
            </div>
            <button className="next-question-btn" onClick={generateQuestion}>
              Next Question
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </QuizModal>
  );
}

export default DamageCalcQuiz; 